import React, { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';

const productsMenu = [
  {
    name: 'CloudRebuild',
    to: '/products/cloudrebuild',
    desc: 'Multi-cloud backup & DR — Azure, AWS, GCP, OCI, M365',
    badge: 'New',
    initials: 'CR',
    accent: 'from-amber-500/30 to-amber-500/5'
  },
  {
    name: 'Zaatly',
    to: '/products/zaatly',
    desc: 'AI-native CRM built on Microsoft 365',
    badge: 'Featured',
    initials: 'Za',
    accent: 'from-sky-500/30 to-sky-500/5'
  }
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  const hashLink = (hash) => (isHome ? `#${hash}` : `/#${hash}`);

  const links = [
    { label: 'Home', to: hashLink('home'), isRoute: false },
    { label: 'About', to: hashLink('about'), isRoute: false },
    { label: 'Services', to: hashLink('services'), isRoute: false },
    { label: 'AI Products', to: '/ai-products', isRoute: true },
    { label: 'Case Studies', to: hashLink('case-studies'), isRoute: false }
  ];

  const renderLink = (l, cls) => {
    if (l.isRoute) return <Link to={l.to} onClick={() => setOpen(false)} className={cls}>{l.label}</Link>;
    return <a href={l.to} onClick={() => setOpen(false)} className={cls}>{l.label}</a>;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#05080f]/70 border-b border-white/5">
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-24">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_cloud-ai-platform-1/artifacts/b16wgngd_Black%20and%20Gold%20Elegant%20Luxury%20Flower%20Jewelry%20Logo.png"
            alt="Pratham Innovations"
            className="h-16 md:h-20 w-auto object-contain shrink-0"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display font-semibold text-lg md:text-xl tracking-tight gradient-text">
              Pratham Innovations
            </span>
            <span className="text-[10px] md:text-[11px] text-amber-200/70 italic tracking-wide -mt-0.5">
              Where imagination becomes reality
            </span>
          </div>
        </Link>

        <ul className="hidden md:flex items-center gap-7 lg:gap-9">
          {links.map((l) => {
            const active = l.isRoute && location.pathname === l.to;
            const linkItem = (
              <li key={l.label}>
                {renderLink(
                  l,
                  `text-sm transition-colors ${active ? 'text-amber-400' : 'text-slate-300 hover:text-white'}`
                )}
              </li>
            );

            if (l.label !== 'AI Products') return linkItem;

            return (
              <React.Fragment key={l.label}>
                {linkItem}
                {/* Products dropdown - positioned right after AI Products */}
                <li
                  key="products-dropdown"
                  className="relative"
                  onMouseEnter={() => setProductsOpen(true)}
                  onMouseLeave={() => setProductsOpen(false)}
                >
                  <button
                    className={`inline-flex items-center gap-1 text-sm transition-colors ${
                      location.pathname.startsWith('/products') ? 'text-amber-400' : 'text-slate-300 hover:text-white'
                    }`}
                    onClick={() => setProductsOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={productsOpen}
                    data-testid="products-dropdown-btn"
                  >
                    Products <ChevronDown size={14} className={`transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {productsOpen && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-80">
                      <div className="card-dark rounded-2xl p-2 border border-white/10 shadow-2xl">
                        {productsMenu.map((p) => (
                          <Link
                            key={p.name}
                            to={p.to}
                            onClick={() => setProductsOpen(false)}
                            className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-amber-500/10 transition-colors group"
                            data-testid={`products-menu-${p.name.toLowerCase()}`}
                          >
                            <div className={`h-12 w-12 shrink-0 rounded-xl bg-gradient-to-br ${p.accent} border border-white/10 flex items-center justify-center`}>
                              <span className="text-amber-200 font-display font-semibold text-sm tracking-wide">{p.initials}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="text-white text-sm font-semibold group-hover:text-amber-300 truncate">{p.name}</div>
                                {p.badge && (
                                  <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25">
                                    {p.badge}
                                  </span>
                                )}
                              </div>
                              <div className="text-slate-400 text-xs mt-0.5 line-clamp-2">{p.desc}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ul>

        <div className="hidden md:block">
          <a href={hashLink('contact')}>
            <Button className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white h-10 px-5">
              Contact Us
            </Button>
          </a>
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden border-t border-white/5 bg-[#05080f]">
          <ul className="flex flex-col p-4 gap-3">
            {links.map((l) => {
              const linkLi = (
                <li key={l.label}>
                  {renderLink(l, 'block text-slate-300 hover:text-white py-2')}
                </li>
              );
              if (l.label !== 'AI Products') return linkLi;
              return (
                <React.Fragment key={l.label}>
                  {linkLi}
                  <li key="mobile-products">
                    <button
                      onClick={() => setMobileProductsOpen((v) => !v)}
                      className="w-full flex items-center justify-between text-slate-300 hover:text-white py-2"
                    >
                      Products <ChevronDown size={16} className={`transition-transform ${mobileProductsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileProductsOpen && (
                      <div className="ml-3 mt-1 space-y-1 border-l border-amber-500/20 pl-3">
                        {productsMenu.map((p) => (
                          <Link key={p.name} to={p.to} onClick={() => setOpen(false)} className="flex items-center gap-3 py-2">
                            <div className={`h-9 w-9 shrink-0 rounded-lg bg-gradient-to-br ${p.accent} border border-white/10 flex items-center justify-center`}>
                              <span className="text-amber-200 font-display font-semibold text-xs">{p.initials}</span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <div className="text-white text-sm">{p.name}</div>
                                {p.badge && (
                                  <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25">
                                    {p.badge}
                                  </span>
                                )}
                              </div>
                              <div className="text-slate-500 text-xs">{p.desc}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </li>
                </React.Fragment>
              );
            })}
            <li>
              <a href={hashLink('contact')} onClick={() => setOpen(false)}>
                <Button className="w-full rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white">
                  Contact Us
                </Button>
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Navbar;
